import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { RenderedHtml } from 'modules/editor';
import { cn } from 'lib/utils';
import { countWords, MIN_WORDS } from '../../constants';
import type { CandidateQuestion, IeltsWritingTask } from '../../types';
import { ResizableSplit } from './ResizableSplit';

interface Props {
  question: CandidateQuestion;
  value: string;
  onChange: (value: string) => void;
}

/**
 * IELTS Writing: the task on the left, the script on the right, with a live
 * word count. The divider between them can be dragged.
 */
export function WritingTask({ question, value, onChange }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const task: IeltsWritingTask = question.writingTask ?? 'TASK_2';
  const words = countWords(value);
  const minimum = MIN_WORDS[task];
  const under = words > 0 && words < minimum;

  const promptPane = (
    <section aria-label={t('exam.writing.task_prompt')} className="min-h-0 flex-1 overflow-y-auto bg-[#FBFAF7] p-5 sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        {task === 'TASK_1' ? t('exam.writing.task1') : t('exam.writing.task2')}
      </p>
      <p className="mt-1 text-sm text-gray-600">
        {t('exam.writing.guidance', { minutes: task === 'TASK_1' ? 20 : 40, words: minimum })}
      </p>
      <div className="mt-4 text-[15px] leading-relaxed text-gray-900">
        <RenderedHtml html={question.prompt || ''} />
      </div>
      {question.mediaUrl && (
        <img src={question.mediaUrl} alt={t('exam.writing.task_visual')} className="mt-4 max-w-full rounded-md border border-gray-200" />
      )}
    </section>
  );

  const answerPane = (
    <section aria-label={t('exam.writing.your_answer')} className="flex min-h-0 flex-1 flex-col bg-white">
      <label htmlFor={id} className="sr-only">
        {t('exam.writing.your_answer')}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="sentences"
        aria-describedby={`${id}-count`}
        // Edge to edge, so the global focus outline (drawn outside) would be clipped: draw it inside.
        className="min-h-0 flex-1 resize-none p-5 font-serif text-[16px] leading-7 text-gray-900 focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:p-8"
        placeholder={t('exam.writing.placeholder')}
      />
      <div
        id={`${id}-count`}
        className="flex items-center justify-between border-t border-gray-200 px-5 py-2.5 text-sm"
        aria-live="polite"
      >
        <span className={cn('font-medium', under ? 'text-amber-700' : 'text-gray-700')}>
          {t('exam.writing.word_count', { count: words })}
        </span>
        <span className="text-gray-500">{t('exam.writing.minimum', { count: minimum })}</span>
      </div>
    </section>
  );

  return <ResizableSplit handleLabel={t('exam.resize_panes')} start={promptPane} end={answerPane} />;
}

export default WritingTask;
